import React from 'react'
import { Box, Button, Typography, useTheme } from '@mui/material'
import {
  IconUserPlus,
  IconArrowNarrowRight,
} from '@tabler/icons-react'
import { useAuth0 } from '@auth0/auth0-react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAppSelector } from '../../store'
import { selectLogin } from '../../store/authSlice'
import { ROUTES } from '../../helpers/routes-helper'
import { logEvent } from '../../analytics/analytics'
import { ONBOARD_PATH_PARAMS } from '../../helpers/onboard-helper'
import { UserType } from '../../types'
import useRudderStackAnalytics from '../../useRudderAnalytics'
import { usePromoModal } from '../../context/PromoModalContext'
import { trackPopupClicked } from '../../utils/popupAnalytics'

const FeatureBox: React.FC<{ icon: React.ReactNode; text: string }> = ({
  icon,
  text,
}) => {
  const theme = useTheme()

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        alignItems: 'flex-start',
        backgroundColor: theme.palette.primary[100],
        px: '24px',
        py: '20px',
        borderRadius: '16px',
      }}
    >
      <Box
        sx={{
          backgroundColor: theme.palette.background.paper,
          p: 1.5,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.05)',
        }}
      >
        {icon}
      </Box>
      <Typography
        variant="body1"
        fontWeight={500}
        sx={{ fontSize: '16px', lineHeight: 1.5 }}
      >
        {text}
      </Typography>
    </Box>
  )
}

const WebPromo: React.FC = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const params = useParams()
  const { isAuthenticated, loginWithRedirect } = useAuth0()
  const loginResponse = useAppSelector(selectLogin)
  const [analytics, isAnalyticsReady] = useRudderStackAnalytics()
  const { lastTrigger, lastPage } = usePromoModal()

  const handleStartEarning = async (): Promise<void> => {
    // Determine destination
    let destination: 'login' | 'points' | 'onboard' = 'login'
    if (!isAuthenticated) {
      destination = 'login'
    } else {
      const eaterDTO = loginResponse?.eaterDTO
      const type = loginResponse?.type
      if (eaterDTO?.step === 'DONE' && type === UserType.EATER) {
        destination = 'points'
      } else {
        destination = 'onboard'
      }
    }

    // Track analytics with normalized payload
    const restaurantAlias = params.alias ?? null
    trackPopupClicked(
      {
        page: lastPage !== 'unknown' ? lastPage : undefined,
        trigger: lastTrigger !== 'unknown' ? lastTrigger : 'manual',
        modalType: 'web',
        destination,
        restaurantAlias,
      },
      analytics,
      isAnalyticsReady,
    )

    if (!isAuthenticated) {
      // Signup funnel entry (web promo redirects to the Auth0 signup screen).
      logEvent('signup_started', {
        screen_name: 'web_promo',
        source: 'web_promo',
        mode: 'signup',
      })
      // Logged out: navigate to login/signup
      await loginWithRedirect({
        authorizationParams: { screen_hint: 'signup' },
        appState: {
          returnTo: ROUTES.HOME,
        },
      })
    } else {
      // Logged in: check onboarding status
      const eaterDTO = loginResponse?.eaterDTO
      const type = loginResponse?.type

      if (eaterDTO?.step === 'DONE' && type === UserType.EATER) {
        // Onboarded: navigate to Points page
        navigate(ROUTES.POINTS)
      } else {
        // Not onboarded: navigate to onboarding
        navigate(`${ROUTES.ONBOARD}/${ONBOARD_PATH_PARAMS.CHOOSE_ROLE}`)
      }
    }
  }

  return (
    <>
      <Typography
        variant="h2"
        fontWeight={800}
        sx={{
          mb: 3,
          fontSize: { xs: '26px !important', md: '36px !important' },
          textAlign: 'center',
          lineHeight: { xs: 1.1 },
          position: 'relative',
          px: { xs: 3, sm: 0 },
          zIndex: 110,
        }}
      >
        Help restaurants understand dining behavior
      </Typography>

      <Typography
        variant="body1"
        sx={{
          mb: 2,
          textAlign: 'center',
          color: 'text.secondary',
          px: { xs: 2, sm: 0 },
          lineHeight: 1.6,
        }}
      >
        Restaurants use real dining data to make better decisions. Your activity helps them understand what people order and how menus perform. As restaurants pay for these insights, a portion of that value flows back to you.
      </Typography>
      <Typography
        variant="body2"
        sx={{
          mb: 4,
          textAlign: 'center',
          color: 'text.secondary',
          px: { xs: 2, sm: 0 },
          fontSize: '0.875rem',
        }}
      >
        Your activity is aggregated — never personal.
      </Typography>

      <Box
        sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 3 } }}
      >
        <FeatureBox
          icon={<IconUserPlus stroke={2} size={22} />}
          text="Create a free account to track your dining activity."
        />
        <FeatureBox
          icon={
            <Box
              sx={{
                backgroundColor: theme.palette.background.paper,
                p: 1.5,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.05)',
              }}
            >
              <Typography
                sx={{
                  fontSize: '18px',
                  fontWeight: 700,
                  color: theme.palette.primary.main,
                }}
              >
                ⭐
              </Typography>
            </Box>
          }
          text="No reviews required. Your dining behavior is the data."
        />
      </Box>

      <Box
        sx={{
          pt: 5,
          pb: { xs: 2, sm: 4 },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Button
          variant="contained"
          sx={{ px: 3 }}
          endIcon={<IconArrowNarrowRight stroke={2} />}
          onClick={() => {
            void handleStartEarning()
          }}
        >
          Create an account
        </Button>
      </Box>
    </>
  )
}

export default WebPromo
